import * as React from 'react';
import { useConfig, useContractContext, useFeatures, useStrings } from 'src/providers';
import { useCurrencyFormatter } from 'src/hooks/useCurrencyFormatter';
import { FormattedString } from 'src/components/Shared/FormattedString';
import { Header, HeaderPromptTypes } from 'src/components/Prompts/Header';
import { Footer } from 'src/components/Prompts/Footer';
import { Disclaimer } from 'src/components/Disclaimer';
import { StyledVerticalPrompt, StyledVerticalOption } from './StyledVertical';
import { ContractDetailsRequestedPromptOptions } from 'src/types/IModal';
import { OptionInputStyles, OptionInputTypes } from 'src/types/IOptions';

interface IVerticalPromptProps {
  contractDetailsRequested: (prompt: ContractDetailsRequestedPromptOptions) => void;
  isList?: boolean;
}

export function VerticalPrompt(props: IVerticalPromptProps) {
  const formatCurrency = useCurrencyFormatter();
  const { contractDetailsRequested, isList } = props;
  const { contracts, selectDecline, selectedContractId, toggleContract, ipidUrl } = useContractContext();
  const { forceVisualCheckboxes, showDeclineOption, showIpidDisclaimer } = useFeatures();
  const { declineOption, headline, termLabel, learnMoreLink } = useStrings();
  const { showAgeRestrictionDisclaimer, phrases } = useConfig();

  const inputStyle = React.useMemo(() => {
    if (isList) return OptionInputStyles.HIDDEN;
    if ((forceVisualCheckboxes || contracts.length === 1) && !showDeclineOption) {
      return OptionInputStyles.CHECKBOX;
    }
    return OptionInputStyles.RADIO;
  }, [contracts, isList, showDeclineOption, OptionInputStyles, forceVisualCheckboxes]);

  const inputType = React.useMemo(
    () => (isList || !showDeclineOption ? OptionInputTypes.CHECKBOX : OptionInputTypes.RADIO),
    [isList, OptionInputTypes, showDeclineOption]
  );

  React.useEffect(() => {
    if (showDeclineOption && contracts.length > 0 && !selectedContractId) {
      // If there's an explicit decline option, select it by default
      // TODO: don't fire events in the default case
      selectDecline();
    }
  }, [showDeclineOption, contracts, selectedContractId, selectDecline]);

  const handleContractDetailsRequested = React.useCallback(() => {
    const value = ContractDetailsRequestedPromptOptions[isList ? 'LIST' : 'RADIO'];
    contractDetailsRequested(value);
  }, [contractDetailsRequested, ContractDetailsRequestedPromptOptions, isList]);

  const promptType = isList ? 'list' : 'radio';

  return (
    <StyledVerticalPrompt data-test={`product-page-${promptType}`}>
      <Header
        promptType={isList ? HeaderPromptTypes.LIST : HeaderPromptTypes.RADIO}
        learnMoreLink={learnMoreLink}
        contractDetailsRequested={handleContractDetailsRequested}
        showLearnMore={showIpidDisclaimer}
      >
        <span>{headline}</span>
      </Header>
      <ul
        className="contracts"
        data-test={`pdp-contracts-${promptType}`}
        id={`contracts-${promptType}`}
      >
        {contracts.map((contract) => (
          <StyledVerticalOption
            contractId={contract.id}
            inputStyle={inputStyle}
            inputType={inputType}
            isSelected={selectedContractId === contract.id}
            key={contract.id}
            onChange={toggleContract}
          >
            <FormattedString
              className="text"
              string={termLabel}
              tagName="span"
              values={{ termSingular: contract.termLabelSingular }}
            />
            <span className="price">
              {formatCurrency(+contract.price)}
            </span>
          </StyledVerticalOption>
        ))}
        { showDeclineOption && (
          <StyledVerticalOption
            contractId="decline"
            inputStyle={inputStyle}
            inputType={inputType}
            isSelected={selectedContractId === 'decline'}
            onChange={selectDecline}
          >
            <span className="text">{declineOption}</span>
          </StyledVerticalOption>
        ) }
      </ul>
      {(showAgeRestrictionDisclaimer || showIpidDisclaimer) && (
        <Disclaimer
          disclaimer={showAgeRestrictionDisclaimer ? phrases['modal.ageRestrictionDisclaimer'] : null}
          ipidUrl={showIpidDisclaimer ? ipidUrl : null}
        />
      )}
      <Footer
        contractDetailsRequested={handleContractDetailsRequested}
        showLearnMore={!showIpidDisclaimer}
      />
    </StyledVerticalPrompt>
  );
}
